import { PaginationProps, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { trim } from "lodash";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmPopup from "../../commons/components/ConfirmPopup/ConfirmPopup";
import CustomPagination from "../../commons/components/CustomPagination";
import CustomTable from "../../commons/components/CustomTable";
import GradientText from "../../commons/components/GradientText/GradientText";
import PrimaryButton from "../../commons/components/PrimaryButton/PrimaryButton";
import { SearchInput } from "../../commons/components/SearchInput";
import {
  ORDER_TRACKING_CONFIRM_URL,
  ORDER_TRACKING_URL,
  PRE_ORDER_TRACKING_URL,
  URL_GET_CHAT,
} from "../../commons/constants/api-urls";
import { getOrderStatus, ORDER_STATUS, OrderTrackingType } from "../../commons/constants/order";
import { routerNavigation } from "../../commons/constants/routers";
import useFetchList from "../../commons/hooks/useFetchList";
import useToast from "../../commons/hooks/useToast";
import { NoteIcon } from "../../commons/resources";
import defaultAxios from "../../commons/utils/axios";
import { format2Digit } from "../../commons/utils/functions/formatPrice";
import { AppContext } from "../../contexts/AppContext";
import CustomTabs from "../../commons/components/CustomTabs";

const OrderList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { currentStore, findAndSetStore } = useContext(AppContext);
  const [showConfirmOrder, setShowConfirmOrder] = useState(false);
  const [selectOrderId, setSelectOrderId] = useState("");
  const [activeTab, setActiveTab] = useState(OrderTrackingType.ORDER);

  const { storeId } = useParams<{ storeId: string }>();

  useEffect(() => {
    if (!storeId) return;
    findAndSetStore(storeId);
  }, [storeId]);

  const [numPerPage, setNumPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [statuses, setStatuses] = useState();
  const [searchKey, setSearchKey] = useState("");

  const params = {
    perPage: numPerPage,
    paginationMetadataStyle: "body",
    page: currentPage,
    search_value: searchKey,
    status: statuses,
  };

  let urlOrder = storeId ? ORDER_TRACKING_URL(storeId as string) : "";
  const {
    data: orderList,
    total: totalOrder,
    refresh: refreshListOrder,
  } = useFetchList<MerchantOrderResponse>(urlOrder, params);

  let urlPreOrder = storeId ? PRE_ORDER_TRACKING_URL(storeId as string) : "";
  const {
    data: preOrderList,
    total: totalPreOrder,
    refresh: refreshListPreOrder,
  } = useFetchList<MerchantOrderResponse>(urlPreOrder, params);

  const orders: any[] = useMemo(() => {
    const data = activeTab === OrderTrackingType.ORDER ? orderList : preOrderList;
    return data?.map((order: MerchantOrderResponse) => {
      return {
        create_time: order.create_time,
        id: order.id,
        name: order.store.merchant.name,
        note: order.note,
        phone: order.shipments?.[0]?.data?.recipient?.phone,
        address: order.shipments?.[0]?.address,
        status: getOrderStatus(order.status),
        originalStatus: order.status,
        items: order.items,
        payment: order.payment,
      };
    });
  }, [activeTab, orderList, preOrderList]);

  const getTotal = useMemo(() => {
    switch (activeTab) {
      case OrderTrackingType.ORDER:
        return totalOrder;
      case OrderTrackingType.PRE_ORDER:
        return totalPreOrder;
    }
  }, [activeTab, totalOrder, totalPreOrder]);

  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    setCurrentPage(current);
    setNumPerPage(pageSize);
  };

  function onSearch(value: string) {
    if (value !== searchKey) {
      setSearchKey(value);
      setCurrentPage(1);
    }
  }

  const confirmOrder = async () => {
    if (!selectOrderId) {
      toast.error("No order selected");
      return;
    }
    const url = ORDER_TRACKING_CONFIRM_URL(selectOrderId);
    await defaultAxios.put<{}>(url, {});
    refreshListOrder();
    setSelectOrderId("");
    setShowConfirmOrder(false);
    toast.success("Confirm order success");
  };

  function pageChange(page: number, pageSize: number) {
    setCurrentPage(page);
    setNumPerPage(pageSize);
  }

  const linKtoOrderDetail = useCallback(
    (valueId: number) => {
      navigate(routerNavigation.STORES.ORDER_TRACKING_DETAIL(storeId ? storeId : "", valueId));
    },
    [storeId]
  );

  function onClickConfirmOrder(id: string) {
    setSelectOrderId(id);
    setShowConfirmOrder(true);
  }

  async function handleGetChat(orderId: number) {
    try {
      const res: any = await defaultAxios.get(URL_GET_CHAT(orderId));
      window.open(res.data.url);
    } catch (error) {
      console.log(error);
    }
  }

  function onChangeTable(pagination: any, filters: any) {
    if (filters.status) {
      setStatuses(filters.status.join(","));
      setCurrentPage(1);
    } else {
      setCurrentPage(1);
      setStatuses(undefined);
    }
  }

  const handleTabChange = (key: string) => {
    setActiveTab(key as OrderTrackingType);
    setCurrentPage(1);
    if (key === OrderTrackingType.ORDER) {
      refreshListOrder();
    } else {
      refreshListPreOrder();
    }
  };

  const dataSource = useMemo(
    () =>
      orders.map((value, index) => ({
        key: <div onClick={() => linKtoOrderDetail(value.id)}>{value.id}</div>,
        order: (
          <div
            style={{ display: "flex", alignItems: "center", gap: "16px" }}
            onClick={() => linKtoOrderDetail(value.id)}
          >
            {value.id}
            {value.note && (
              <Tooltip
                placement="top"
                title={value.note}
                arrow={{
                  pointAtCenter: true,
                }}
              >
                <div style={{ cursor: "pointer" }}>
                  <NoteIcon />
                </div>
              </Tooltip>
            )}
          </div>
        ),
        phoneNumber: <div onClick={() => linKtoOrderDetail(value.id)}>{value.phone}</div>,
        deliveryAddress: <div onClick={() => linKtoOrderDetail(value.id)}>{value.address}</div>,
        status:
          value.status === ORDER_STATUS.ON_GOING.label ? (
            <GradientText text={value.status} onClick={() => linKtoOrderDetail(value.id)} />
          ) : (
            <div className={`status__${value.status}`} onClick={() => linKtoOrderDetail(value.id)}>
              {value.status}
            </div>
          ),
        amount: <div onClick={() => linKtoOrderDetail(value.id)}>{format2Digit(value.payment.amount)}</div>,
        paymentMethod: <div onClick={() => linKtoOrderDetail(value.id)}>{value.payment.type}</div>,
        time: (
          <div onClick={() => linKtoOrderDetail(value.id)}>
            {moment(value.create_time).format("DD/MM/YYYY") + ", " + moment(value.create_time).format("HH:mm")}
          </div>
        ),
        chat: (
          <div>
            <PrimaryButton onClick={() => handleGetChat(value.id)}>Chat</PrimaryButton>
          </div>
        ),
        action: (
          <div>
            {value && value.originalStatus === ORDER_STATUS.ON_GOING.value && (
              <PrimaryButton onClick={() => onClickConfirmOrder(value.id)} style={{ padding: "0px 0px" }}>
                Confirm Order
              </PrimaryButton>
            )}
          </div>
        ),
      })),
    [linKtoOrderDetail, orders]
  );

  const Tabs = [
    {
      title: "Order List",
      key: OrderTrackingType.ORDER,
      children: <span>({totalOrder})</span>,
    },

    {
      title: "Pre-Order List",
      key: OrderTrackingType.PRE_ORDER,
      children: <span> ({totalPreOrder})</span>,
    },
  ];

  const columns: ColumnsType<any> = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => <div>{text}</div>,
      filters: [
        {
          text: "completed",
          value: ORDER_STATUS.COMPLETED.value,
        },
        {
          text: "cancelled",
          value: ORDER_STATUS.CANCELLED.value,
        },
      ],
      width: 110,
    },
    {
      title: "Order ID",
      dataIndex: "order",
      key: "order",
      render: (text, record) => <div>{text}</div>,
      width: 150,
    },
    {
      title: "Phone number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (text, record) => <div>{text}</div>,
    },
    {
      title: "Delivery address",
      dataIndex: "deliveryAddress",
      key: "deliveryAddress",
      render: (text, record) => <div>{text}</div>,
    },

    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => <div>{text}</div>,
    },

    {
      title: "Payment method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (text, record) => <div>{text}</div>,
    },

    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      render: (text, record) => <div>{text}</div>,
    },
    {
      title: "Chat with customer",
      dataIndex: "chat",
      key: "chat",
      render: (text, record) => <div>{text}</div>,
    },
  ];

  return (
    <div className="order_detail_page">
      <h3>{`${trim(currentStore?.name)} - Order List`}</h3>

      <SearchInput placeholder="Search for order ID" type="search" onSearch={value => onSearch(value)} />

      <div className="order-panel">
        <CustomTabs tabStyle="custom-tab" activeTab={activeTab} onChange={handleTabChange} tabs={Tabs} width="400px">
          <CustomTable
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            className="tab-content"
            scroll={{ x: 400 }}
            style={{ cursor: "pointer" }}
            onChange={onChangeTable}
          />
        </CustomTabs>

        <div className="pagination">
          <CustomPagination
            defaultCurrent={1}
            showSizeChanger
            current={currentPage}
            onChange={pageChange}
            total={getTotal}
            onShowSizeChange={onShowSizeChange}
          />
        </div>
      </div>

      <ConfirmPopup
        open={showConfirmOrder}
        onCancel={() => setShowConfirmOrder(false)}
        title="Confirm Order"
        description={`Are you sure you want to confirm?`}
        onConfirm={() => confirmOrder()}
      />
    </div>
  );
};

export default OrderList;
